<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import * as changeCase from 'change-case';
  import { useForm } from 'vee-validate';
  import { computed, onBeforeMount, ref } from 'vue';
  import * as zod from 'zod';

  import { ExternalRole, InternalRole } from '@stuut/api/models';
  import type { Contact, CreateContactSchema, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { FormControl, FormField, FormItem, FormLabel } from '@ui/components/form';
  import { Input } from '@ui/components/input';
  import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import { PhoneInput } from '@/components/phone-input';

  export interface ContactEditorProps {
    contact?: Contact | null;
    createWithPartner?: boolean;
    disablePartnerSelect?: boolean;
    email?: string;
    /** `two-column`: pairs name, email/phone, job/role on `md+` screens. */
    layout?: 'default' | 'two-column';
    mode?: 'external' | 'internal';
    partner?: Partner;
    showPartnerSelect?: boolean;
  }

  const props = withDefaults(defineProps<ContactEditorProps>(), {
    layout: 'default',
  });

  const emits = defineEmits<{
    (e: 'cancel'): void;
    (e: 'saved', contact: Contact): void;
  }>();

  const mode = props.mode ?? 'external';

  const fieldsGridClass = computed(() =>
    props.layout === 'two-column'
      ? 'grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2'
      : 'grid gap-6',
  );

  const formState = ref<{
    didValidate: boolean;
    errors: Record<string, string> | null;
  }>({
    didValidate: false,
    errors: null,
  });

  const isSubmitting = ref(false);
  const errorMessage = ref<string>();
  const partners = ref<Partner[]>([]);

  /** When `showPartnerSelect` is false, parent owns partner selection (e.g. triage panel). */
  const showPartnerUi = computed(() => props.showPartnerSelect ?? !props.partner);

  // Update the zodObj definition to use a type
  interface FormSchema {
    email: string;
    externalRole?: ExternalRole | null;
    familyName?: string;
    givenName?: string;
    internalRole?: InternalRole | null;
    jobTitle?: string;
    partner?: string;
    phone?: string | null;
  }

  const zodBaseObj: Omit<Record<keyof FormSchema, zod.ZodType>, 'internalRole' | 'externalRole'> = {
    email: zod.string().email().min(1),
    familyName: zod.string().optional(),
    givenName: zod.string().optional(),
    jobTitle: zod.string().optional(),
    partner: zod.string().optional(), // Always include partner field, but make it optional
    phone: zod.string().nullish(),
  };

  const form = useForm<FormSchema>({
    initialValues: {
      email: props.contact?.email ?? props.email ?? undefined,
      externalRole: props.contact?.external_role,
      familyName: props.contact?.family_name ?? undefined,
      givenName: props.contact?.given_name ?? undefined,
      internalRole: props.contact?.internal_role,
      jobTitle: props.contact?.job_title ?? undefined,
      partner: props.contact?.partner_id ?? undefined,
      phone: props.contact?.phone ?? null,
    },
    validationSchema: computed(() => {
      let schema = zod.object(zodBaseObj);

      if (mode === 'internal') {
        schema = schema.extend({
          internalRole: zod.nativeEnum(InternalRole, {
            required_error: 'Internal role is required.',
          }),
        });
      } else {
        schema = schema.extend({
          externalRole: zod.nativeEnum(ExternalRole).nullish(),
        });
      }

      return toTypedSchema(schema);
    }),
  });

  const selectedPartner = computed(() => {
    return partners.value?.find((partner) => partner.id === form.values?.partner);
  });

  const isPrimaryEmailContact = computed(() => {
    return props.contact?.is_primary_email === true;
  });
  const roleOptions = computed(() => {
    return mode === 'internal'
      ? [
          { label: 'Account Manager', value: InternalRole.ACCOUNT_MANAGER },
          { label: 'Account Executive', value: InternalRole.ACCOUNT_EXECUTIVE },
          { label: 'CSM', value: InternalRole.CSM },
        ]
      : [
          { label: 'Billing Contact', value: ExternalRole.BILLING_CONTACT },
          { label: 'Executive', value: ExternalRole.EXECUTIVE },
          { label: 'Economic User', value: ExternalRole.ECONOMIC_USER },
        ];
  });

  const submitContact = async (): Promise<Contact | null> => {
    if (isSubmitting.value) return null;
    isSubmitting.value = true;

    try {
      const result = await form.validate();
      if (!(result.valid && result.values)) {
        formState.value.didValidate = true;
        formState.value.errors = result.errors;
        return null;
      }

      const data: Partial<CreateContactSchema> = {};
      const values = result.values || {};
      Object.keys(values).forEach((k) => {
        if (k !== 'partner' && values[k] !== undefined) {
          data[changeCase.snakeCase(k)] = values[k]?.trim ? values[k].trim() : values[k];
        }
      });

      const _partner = props.partner || partners.value?.find((p) => p.id === form.values?.partner);

      if (!_partner) {
        toast.error('Please select a partner');
        return null;
      }

      let contact: Contact;
      if (props.contact) {
        if (props.createWithPartner) {
          contact = await stuut.partners.createContact(_partner, data as CreateContactSchema);
        } else {
          contact = await stuut.partners.updateContact(_partner, props.contact, data);
        }
      } else {
        contact = await stuut.partners.createContact(_partner, data as CreateContactSchema);
      }

      emits('saved', contact);
      return contact;
    } catch (err: any) {
      toast.error(err.clientMessage || 'Failed to save contact.');
      return null;
    } finally {
      isSubmitting.value = false;
    }
  };

  const onSubmit = (e: Event) => {
    e.preventDefault();
    void submitContact();
  };

  defineExpose({ submit: submitContact });

  onBeforeMount(async () => {
    if (showPartnerUi.value) {
      partners.value = await stuut.partners.list();

      if (props.contact?.partner_id) {
        form.setFieldValue('partner', props.contact?.partner_id);
      }
    }
  });
</script>

<template>
  <form class="relative flex flex-col gap-6" @submit="onSubmit">
    <p v-if="errorMessage" class="text-center text-sm text-destructive">
      {{ errorMessage }}
    </p>

    <FormField v-if="partners?.length" v-slot="{}" name="partner">
      <FormItem>
        <FormLabel>Partner Name</FormLabel>

        <Select
          class="grow px-1 h-5 cursor-default"
          :model-value="form.values.partner"
          @update:model-value="(event) => form.setFieldValue('partner', event)"
        >
          <FormControl>
            <SelectTrigger class="w-full" :disabled="props.disablePartnerSelect">
              <SelectValue />
            </SelectTrigger>
          </FormControl>

          <SelectContent>
            <SelectGroup>
              <SelectItem v-for="partner in partners" :key="partner?.id" :value="partner.id">
                {{ partner?.name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormItem>
    </FormField>

    <div :class="fieldsGridClass">
      <FormField v-slot="{ componentField }" name="givenName">
        <FormItem>
          <FormLabel> First Name</FormLabel>

          <FormControl>
            <Input
              type="text"
              v-bind="componentField"
              :disabled="isSubmitting"
              placeholder="Jane"
            />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="familyName">
        <FormItem>
          <FormLabel> Last Name </FormLabel>

          <FormControl>
            <Input
              type="text"
              v-bind="componentField"
              :disabled="isSubmitting"
              placeholder="Smith"
            />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        :validate-on-input="false"
        :disabled="isSubmitting"
        name="email"
      >
        <FormItem>
          <FormLabel>Email</FormLabel>

          <FormControl>
            <Input
              type="email"
              v-bind="componentField"
              :disabled="isSubmitting"
              placeholder="name@example.com"
              auto-capitalize="none"
              auto-complete="email"
              auto-correct="off"
            />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ value }"
        :validate-on-input="false"
        :disabled="isSubmitting"
        name="phone"
      >
        <FormItem>
          <FormLabel>Phone Number</FormLabel>

          <FormControl>
            <PhoneInput
              :disabled="isSubmitting"
              :default-value="value"
              @change="
                (payload) => {
                  form.setFieldValue('phone', payload.phoneNumber?.format('INTERNATIONAL') ?? null);
                }
              "
            />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="jobTitle">
        <FormItem>
          <FormLabel>Job Title</FormLabel>

          <FormControl>
            <Input
              type="text"
              v-bind="componentField"
              :disabled="isSubmitting"
              placeholder="Supervisor"
            />
          </FormControl>
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField, value }"
        :name="mode === 'internal' ? 'internalRole' : 'externalRole'"
      >
        <FormItem>
          <FormLabel>Role</FormLabel>
          <div class="flex gap-2 items-center">
            <Select v-bind="componentField" :disabled="isSubmitting || isPrimaryEmailContact">
              <FormControl>
                <SelectTrigger class="w-full">
                  <SelectValue
                    :placeholder="
                      isPrimaryEmailContact ? 'Primary Billing Contact' : 'Select a role'
                    "
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectGroup>
                  <SelectItem
                    v-for="option in roleOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ isPrimaryEmailContact ? 'Primary Billing Contact' : option.label }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              v-if="value"
              variant="ghost"
              size="icon"
              @click="
                () =>
                  form.setFieldValue(mode === 'internal' ? 'internalRole' : 'externalRole', null)
              "
            >
              <i class="fa fa-rotate-left" />
            </Button>
          </div>
        </FormItem>
      </FormField>
    </div>

    <template v-if="$slots.contact_editor_actions">
      <slot
        name="contact_editor_actions"
        v-bind="{
          isSubmitting,
          didValidate: formState.didValidate,
          errors: formState.errors,
          cancel: emits('cancel'),
          partner: selectedPartner,
          contact: props.contact,
          createWithPartner: props.createWithPartner,
          onSubmit,
        }"
      />
    </template>

    <div v-else class="flex flex-row gap-x-4">
      <Button
        class="flex-1"
        type="button"
        variant="outline"
        :disabled="isSubmitting"
        @click="emits('cancel')"
      >
        Cancel
      </Button>

      <Button class="flex-1" type="submit" :loading="isSubmitting">
        {{ props.contact ? 'Save' : 'Create' }}
      </Button>
    </div>
  </form>
</template>

<style scoped></style>
